import {
    AfterViewInit,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output,
    ViewChild
} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {EyeTrackerService} from 'projects/eye-tracker/src/lib/eye-tracker.service';
import {ViewportScroller} from '@angular/common';

export interface ITechCheckConfig {
    setUpBrowserText: string;
    chromeBrowserText: string;
    cameraAccessText: string;
    eyesAndHeadInSightText: string;
    startText: string;
    restartText: string;
    agreeText: string;
    libraryDownloadingText: string;
    lookIntoTheCameraText: string;
    listText: string;
    doNotAppearText: string;
    findOutWhyText: string;
    browserNotSupportedText: string;
    useLinkText: string;
    downloadGoogleChromeText: string;
    accessYourCameraText: string;
    gazefilterErrorHeader: string;
    gazefilterErrorText: string;
}

enum Views {
    'faceDetection',
    'webcamDetection',
    'browserDetection'
}

@Component({
    selector: 'lib-technical-check',
    templateUrl: './technical-check.component.html',
    styleUrls: ['./technical-check.component.scss']
})
export class TechnicalCheckComponent implements OnInit, OnDestroy, AfterViewInit {

    @Output() checked: EventEmitter<any> = new EventEmitter<any>();
    @Input() gazefilter: any;
    @Input() isEng = false;
    @Input() isWasmConfigLoaded: any;
    @Input() config: ITechCheckConfig = {
        setUpBrowserText: 'Set up your browser<br/>and camera',
        chromeBrowserText: 'Browser check',
        cameraAccessText: 'Camera access',
        eyesAndHeadInSightText: 'Eyes and head in sight',
        startText: 'Start',
        restartText: 'Restart',
        agreeText: 'I hereby agree to <a href="/terms-and-conditions#legal_pp" target="_blank">Anima’s privacy policy</a> terms and conditions and approve my personal data processing on the legal grounds',
        libraryDownloadingText: 'Library downloading',
        lookIntoTheCameraText: 'Look into the camera, so eyes and head <br>lineaments appear on the screen above',
        listText: '<ul>' +
            '<li>You need Wifi, a computer, a webcam, and browser.</li>\n' +
            '<li>Look into the camera so your eyes and head align with the guidelines that appear on the screen above</li>' +
            '<li>Your face must be visible and properly lit.</li>' +
            '<li>Set your camera below eye level at a distance of <b>30-60</b> cm from your face.</li>' +
            '<li>Make sure the camera is not being used by any other app <br>(for example, Zoom, Google Meet, Skype).</li>' +
            '<li><a class="btn btn-sm p-0 btn-link" href="/#heading8" target="_blank">' +
            'Wearing glasses?' +
            '</a>' +
            '</li>' +
            '</ul>',
        doNotAppearText: 'Lineaments do not appear?',
        findOutWhyText: 'Find out why',
        browserNotSupportedText: 'Your browser is not supported yet, please install Google Chrome to continue',
        useLinkText: 'Use link bellow to download Google Chrome',
        downloadGoogleChromeText: 'Download Google Chrome',
        accessYourCameraText: 'Allow Anima to access your camera',
        gazefilterErrorHeader: 'Помилка ініціалізації бібліотеки ай-трекингу',
        gazefilterErrorText: 'Можливо, на сервер надходить велика кількість запитів. Будь ласка перезавантажте сторінку або поверніться до проходження за деякий час. Наша команда докладає всіх зусиль, щоб сервіс якомога швидше знову заработав.'
    };

    testId = null;

    browserChecked = false;
    chromeConfirmed = false;

    webCamChecked = false;
    webCamAllowed = false;

    faceDetected = true;
    faceDetectionChecked = false;

    isConfirmed = false;
    isDetected: any = [];

    views = Views;
    view: Views;

    redirectTimeout: any = null;
    filterInterval: any = null;

    detection: any = [];
    readyToRedirect = false;

    error = null;
    @ViewChild('trackerCanvas') trackerCanvas: ElementRef;

    constructor(
        public service: EyeTrackerService,
        private router: Router,
        private route: ActivatedRoute,
        private viewportScroller: ViewportScroller
    ) {
        const {test} = route.snapshot.queryParams;
        this.testId = test;
    }

    public get wasmConfigLoaded() {
        return this.service.wasmConfigLoaded;
    }

    hide() {
        this.hideAlert = true;
    }

    set hideAlert(prop: any) {
        localStorage.setItem('hideAlert', prop.toString());
    }

    get hideAlert() {
        return localStorage.getItem('hideAlert');
    }

    get percent() {
        const res = Math.round(this.wasmConfigLoaded * 100 / this.wasmConfigTotal) || 0;
        return res > 99 ? 100 : res;
    }

    get wasmConfigTotal() {
        return this.service.wasmConfigTotal;
    }

    // navigateToFaq(elementId) {
    //     if (this.gazefilter.recorder) {
    //         this.gazefilter.tracker.dispose()
    //             .then(() => this.router.navigate(['/']))
    //             .then(() => {
    //                 setTimeout(() => {
    //                     this.viewportScroller.scrollToAnchor(elementId);
    //                 }, 500);
    //             });
    //
    //     }
    // }

    restart() {
        window.location.reload();
    }

    ngOnInit(): void {
        // this.initChecking();
    }

    ngAfterViewInit() {
        this.initChecking();
    }

    initChecking() {
        this.initVisualizer()
            .then()
            .catch((err) => {
                this.error = err.message;
                switch (err.message) {
                    case 'chromeNotConfirmed':
                        this.browserChecked = true;
                        this.chromeConfirmed = false;
                        return 'chromeNotConfirmed';

                    case 'webCamNotAllowed':
                        this.webCamChecked = true;
                        this.webCamAllowed = false;
                        return 'webCamNotAllowed';
                }
            });
    }

    get browserCheckedState() {
        return !this.browserChecked ? 0 : this.browserChecked && !this.chromeConfirmed ? 1 : 2;
    }

    get webCamCheckedState() {
        return !this.webCamChecked ? 0 : this.webCamChecked && !this.webCamAllowed ? 1 : 2;
    }

    get faceDetectionCheckedState() {
        return !this.faceDetectionChecked ? 0 : this.faceDetectionChecked && (!this.faceDetected || !this.readyToRedirect) ? 1 : 2;
    }

    async initVisualizer() {
        try {
            this.view = Views.browserDetection;
            const browserApproved = await this.service.checkBrowser();
            if (!browserApproved) {
                return false;
            }
            await this.service.waitUntil(1000);
            this.browserChecked = true;
            this.chromeConfirmed = true;
            this.view = Views.webcamDetection;
            await this.service.checkWebCamAllowed();
            // if (!cameraAllowed) {
            //   return false;
            // }
            this.webCamChecked = true;
            this.webCamAllowed = true;
            this.view = Views.faceDetection;

            await this.service.waitUntil(1000);

            const gasInited = await this.service.gazefilterInitedAsPromise();
            if (!gasInited) {
                this.faceDetected = false;
                this.faceDetectionChecked = true;
                this.error = 'gazefilterError';
                return false;
            }

            const canvas = this.trackerCanvas.nativeElement;
            await this.service.initCanvas(this.service.gazefilter, canvas);
            this.addFaceDetectionFilter(this.service.gazefilter);

            this.faceDetectionChecked = true;
        } catch (err) {
            throw err;
        }
    }

    chainError(err) {
        return Promise.reject(err);
    }

    addFaceDetectionFilter(gazefilter) {
        this.filterInterval = setInterval(() => {
            gazefilter.tracker.addListener('filter', this.filterEvent.bind(this));
        }, 1000);
    }


    ngOnDestroy() {
        if (this.filterInterval) {
            clearInterval(this.filterInterval);
        }
        if (this.redirectTimeout) {
            clearTimeout(this.redirectTimeout);
        }
    }

    filterEvent(event: any) {
        this.faceDetected = event.detected;
        if (this.faceDetected) {
            this.detection.push(true);
        } else {
            this.detection = [];
        }

        this.readyToRedirect = this.detection.length > 50;
    }

    onConfirm(prop: boolean) {
        this.isConfirmed = prop;
    }

    initRedirect() {
        this.checked.emit({checked: true, gazefilter: this.gazefilter});

    }

    onStartDown() {

    }

    onStartUp() {

    }
}
